// React
import React, { useEffect, useState, useRef } from "react";

// Libraries
import PropTypes from "prop-types";
import Moment from "moment";
import { Icon, Accordion, Label, Button, Popup, Menu, Form } from "semantic-ui-react";
import { map, filter, includes, every, get, isEmpty, reduce, some, uniqBy } from "lodash";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import List from "react-virtualized/dist/commonjs/List";

// Redux
import { connect } from "react-redux";
import { setCurrentChat, getChatDetails, takeChat, getChatsHistoric } from "@Actions/chats";
import { displayNotification } from "@Redux/Actions/notifications";

// Api
import Api from "@Api";

// Components
import Input from "@Components/Input";
import EmptyList from "@Components/EmptyList";
import PrintRender from "@Components/PrintRender";
import FilterBox from "../FilterBox";
import SectionFilter from "../SectionFilter";
import ChatCard from "../ChatCard";

// Hooks
import usePanelVisibility from "@Hooks/usePanelVisibility";

// Modals
import ChatsFilter from "../../Modals/ChatsFilterHistoric";
import ChatPlantillaModal from "@Features/Monitor/Modals/ChatPlantillaModal";
import ChatListTableModal from "../../Modals/ChatListTableModal";

// Utils
import { staticFilters } from "../../Constants";
import { getChatsFilteredData, getStateQuantity, isEqualItems } from "../../Utils";

// Styles
import styles from "./styles.module.css";

const ChatList = ({
	dispatch,
	isHistoric = false,
	channels,
	groups,
	chats,
	currentChatId,
	currentChat,
	permissions,
	userName,
	userId,
	companyName,
	cmdSearch,
	userState,
	sortInverted,
	toggleSort,
	setHistoricTodayChat,
	historicTodayChat,
}) => {
	const { goChat, goList, isMobile } = usePanelVisibility();
	const [search, setSearch] = useState("");
	const [allItems, setAllItems] = useState([]);
	const [filteredItems, setFilteredItems] = useState([]);
	const [showHistoricToday, setShowHistoricToday] = useState(false);
	const [historicTodayItems, setHistoricTodayItems] = useState([]);
	const [showAllChats, setShowAllChats] = useState(false);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [channelFilters, setChannelFilters] = useState([]);
	const [groupFilters, setGroupFilters] = useState([]);
	const [stateFilters, setStateFilters] = useState([]);
	const [activeChannelFilters, setActiveChannelFilters] = useState([]);
	const [activeGroupFilters, setActiveGroupFilters] = useState([]);
	const [activeStateFilters, setActiveStateFilters] = useState([]);
	const [openChatsFilter, setOpenChatsFilter] = useState(false);
	const [openChatPlantillaModal, setOpenChatPlantillaModal] = useState(false);
	const [openChatListTableModal, setOpenChatListTableModal] = useState(false);

	const printRef = useRef(null);
	const [printAction, setPrintAction] = useState(false);

	const [tagCount, setTagCount] = useState(0);
	const [actionCount, setActionCount] = useState(0);
	const [newCount, setNewCount] = useState(0);
	const [agentCount, setAgentCount] = useState(0);
	const [clientCount, setClientCount] = useState(0);
	const [allCount, setAllCount] = useState(0);
	const accordionContent = useRef(null);
	const hasOpenedFilterOnce = useRef(false);
	// Props for filter
	const [channelfilters, setChannelfilters] = useState([]);
	const [groupsfilters, setGroupsfilters] = useState([]);
	const [tagsSelected, setTagsSelected] = useState([]);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [contacts, setContacts] = useState("");
	const [agents, setAgents] = useState([]);
	const [note, setNote] = useState("");
	const [isFirstLoad, setIsFirstLoad] = useState(true);
	const [dateField, setDateField] = useState("closeDate");
	const [stateValue, setStateValue] = useState("");
	const [orderBy, setOrderBy] = useState("");
	const [orderDirection, setOrderDirection] = useState("");
	const [tabValue, setTabValue] = useState("");

	const isChannelCheckedAll = every(channelFilters, (item) => {
		return item.selected;
	});
	const isGroupCheckedAll = every(groupFilters, (item) => {
		return item.selected;
	});
	const isStateCheckedAll = every(stateFilters, (item) => {
		return item.selected;
	});
	const isNewActive = !isStateCheckedAll && activeStateFilters.includes("Nuevo");
	const isAgentActive = !isStateCheckedAll && activeStateFilters.includes("Atender");
	const isClientActive = !isStateCheckedAll && activeStateFilters.includes("Cliente");
	const isActionActive = activeStateFilters.includes("Action");
	const isTagActive = activeStateFilters.includes("Tag");

	useEffect(() => {
		if (isHistoric) {
			setShowHistoricToday(false);
			setHistoricTodayChat(null);
			setHistoricTodayItems([]);
			setShowAllChats(false);
			setSearch("");
			setFilteredItems([]);
		}
	}, [isHistoric]);

	useEffect(() => {
		if (showHistoricToday) {
			return;
		}

		if (isHistoric && isFirstLoad && !hasOpenedFilterOnce.current) {
			setFilteredItems([]);
			setOpenChatsFilter(true);
			hasOpenedFilterOnce.current = true;
			return;
		}

		const channelsValue = map(channels, (item) => {
			const quantity = filter(chats, (chat) => {
				return get(chat, "channel.label") === item.label;
			}).length;
			return { id: item.label, label: item.label, quantity, selected: false };
		});

		const groupsValue = map(groups, (item) => {
			const quantity = filter(chats, (chat) => {
				return chat.groupId === item.id;
			}).length;
			return { id: item.id, label: item.name, quantity, selected: false };
		});

		const statesValue = map(staticFilters, (item, index) => {
			const quantity = filter(chats, (chat) => {
				return chat.state === item;
			}).length;
			return { id: index, label: item, quantity, selected: true };
		});

		const newCountValue = getStateQuantity(chats, "Nuevo");
		const agentCountValue = getStateQuantity(chats, "Atender");
		const clientCountValue = getStateQuantity(chats, "Cliente");
		const actionCountValue = reduce(
			chats,
			(total, item) => {
				return !isEmpty(item.action) ? total + 1 : total;
			},
			0
		);
		const tagCountValue = reduce(
			chats,
			(total, item) => {
				return some(item.tags, (x) => {
					return x.name === "Plantilla";
				})
					? total + 1
					: total;
			},
			0
		);

		const filteredItemsValue = getChatsFilteredData(chats, activeChannelFilters, activeGroupFilters, activeStateFilters, search);

		setAllItems(chats);
		setFilteredItems(filteredItemsValue);
		setChannelFilters(channelsValue);
		setGroupFilters(groupsValue);
		setStateFilters(statesValue);
		setNewCount(newCountValue);
		setActionCount(actionCountValue);
		setTagCount(tagCountValue);
		setAgentCount(agentCountValue);
		setClientCount(clientCountValue);
		setAllCount(chats?.length || 0);
	}, [chats, isHistoric, showHistoricToday]);

	const getDropdownOptions = (array) => {
		return map(array, (item, index) => {
			return {
				key: index,
				text: item.label,
				value: item.id,
				quantity: item.quantity,
				label: <Label color="blue">{item.quantity}</Label>,
			};
		});
	};

	const handleToggleFilters = () => {
		setIsFiltersOpen(!isFiltersOpen);
	};

	useEffect(() => {
		if (!showHistoricToday) return;
		const items = !showAllChats
			? filter(historicTodayItems, (chat) => {
					return chat.userId === userId;
			  })
			: historicTodayItems;
		setFilteredItems(items);
	}, [showAllChats, historicTodayItems, showHistoricToday, userId]);

	const handleSearch = (e) => {
		const { value } = e.target;
		setSearch(value);
		if (showHistoricToday) {
			const baseItems = !showAllChats
				? filter(historicTodayItems, (chat) => {
						return chat.userId === userId;
				  })
				: historicTodayItems;
			setFilteredItems(getChatsFilteredData(baseItems, [], [], [], value));
		} else if (isHistoric) {
			setFilteredItems(getChatsFilteredData(allItems, [], [], [], value));
		} else {
			setFilteredItems(getChatsFilteredData(allItems, activeChannelFilters, activeGroupFilters, activeStateFilters, value));
		}
	};

	const handleBoxClick = (state) => {
		let newStateFilters = [];
		let newActiveStateFilters = [];
		let withAction = false;
		let withTag = false;
		const hasAll = every(stateFilters, { selected: true });
		switch (state) {
			case "Tag": {
				withTag = true;
				if (hasAll) {
					newStateFilters = map(stateFilters, (item) => {
						return { ...item, selected: false };
					});
					newActiveStateFilters = ["Tag"];
				} else {
					const isInList = activeStateFilters.includes("Tag");
					if (isInList) {
						newActiveStateFilters = filter(activeStateFilters, (item) => {
							return item !== "Tag";
						});
						if (isEmpty(newActiveStateFilters)) {
							newActiveStateFilters = staticFilters;
						}
					} else {
						newActiveStateFilters = [...activeStateFilters, "Tag"];
					}
				}
				break;
			}
			case "Action": {
				withAction = true;
				if (hasAll) {
					newStateFilters = map(stateFilters, (item) => {
						return { ...item, selected: false };
					});
					newActiveStateFilters = ["Action"];
				} else {
					const isInList = activeStateFilters.includes("Action");
					if (isInList) {
						newActiveStateFilters = filter(activeStateFilters, (item) => {
							return item !== "Action";
						});
						if (isEmpty(newActiveStateFilters)) {
							newActiveStateFilters = staticFilters;
						}
					} else {
						newActiveStateFilters = [...activeStateFilters, "Action"];
					}
				}
				break;
			}
			case "Nuevo": {
				if (hasAll) {
					newStateFilters = map(stateFilters, (item) => {
						if (item.id !== "Nuevo") return { ...item, selected: false };
						return item;
					});
					newActiveStateFilters = ["Nuevo"];
				} else {
					const isInList = activeStateFilters.includes("Nuevo");
					newStateFilters = map(stateFilters, (item) => {
						if (item.id === "Nuevo") return { ...item, selected: !item.selected };
						return item;
					});
					if (isInList) {
						newActiveStateFilters = filter(activeStateFilters, (item) => {
							return item !== "Nuevo";
						});
					} else {
						newActiveStateFilters = [...activeStateFilters, "Nuevo"];
					}
				}
				break;
			}
			case "Atender": {
				if (hasAll) {
					newStateFilters = map(stateFilters, (item) => {
						if (item.id !== "Atender") return { ...item, selected: false };
						return item;
					});
					newActiveStateFilters = ["Atender"];
				} else {
					const isInList = activeStateFilters.includes("Atender");
					newStateFilters = map(stateFilters, (item) => {
						if (item.id === "Atender") return { ...item, selected: !item.selected };
						return item;
					});
					if (isInList) {
						newActiveStateFilters = filter(activeStateFilters, (item) => {
							return item !== "Atender";
						});
					} else {
						newActiveStateFilters = [...activeStateFilters, "Atender"];
					}
				}
				break;
			}
			case "Cliente": {
				if (hasAll) {
					newStateFilters = map(stateFilters, (item) => {
						if (item.id !== "Cliente") return { ...item, selected: false };
						return item;
					});
					newActiveStateFilters = ["Cliente"];
				} else {
					const isInList = activeStateFilters.includes("Cliente");
					newStateFilters = map(stateFilters, (item) => {
						if (item.id === "Cliente") return { ...item, selected: !item.selected };
						return item;
					});
					if (isInList) {
						newActiveStateFilters = filter(activeStateFilters, (item) => {
							return item !== "Cliente";
						});
					} else {
						newActiveStateFilters = [...activeStateFilters, "Cliente"];
					}
				}
				break;
			}
			case "Todos": {
				newStateFilters = map(stateFilters, (item) => {
					return { ...item, selected: true };
				});
				newActiveStateFilters = staticFilters;
				break;
			}
			default:
				break;
		}
		const newFilteredItems = getChatsFilteredData(
			allItems,
			activeChannelFilters,
			activeGroupFilters,
			includes(["Action", "Tag"], state) ? [] : newActiveStateFilters,
			search,
			withAction,
			withTag
		);
		setStateFilters(newStateFilters);
		setActiveStateFilters(newActiveStateFilters);
		setFilteredItems(newFilteredItems);
	};

	const handleCheckFilter = (filterList) => {
		let filterListValue = [];
		let activeFiltersValue = [];
		let filteredItemsValue = allItems;

		switch (filterList) {
			case "channelFilters":
				filterListValue = channelFilters;
				break;
			case "groupFilters":
				filterListValue = groupFilters;
				break;
			case "stateFilters":
				filterListValue = stateFilters;
				break;
			default:
				break;
		}
		const isAllChecked = every(filterListValue, (item) => {
			return item.selected;
		});
		const toggledFilterList = map(filterListValue, (item) => {
			return { ...item, selected: !isAllChecked };
		});
		if (!isAllChecked) {
			activeFiltersValue = map(filterListValue, (item) => {
				return item.id;
			});
		}
		switch (filterList) {
			case "channelFilters":
				filteredItemsValue = getChatsFilteredData(allItems, activeFiltersValue, activeGroupFilters, activeStateFilters, search);
				setChannelFilters(toggledFilterList);
				setActiveChannelFilters(activeFiltersValue);
				break;
			case "groupFilters":
				filteredItemsValue = getChatsFilteredData(allItems, activeChannelFilters, activeFiltersValue, activeStateFilters, search);
				setGroupFilters(toggledFilterList);
				setActiveGroupFilters(activeFiltersValue);
				break;
			case "stateFilters":
				filteredItemsValue = getChatsFilteredData(allItems, activeChannelFilters, activeGroupFilters, activeFiltersValue, search);
				setStateFilters(toggledFilterList);
				setActiveStateFilters(activeFiltersValue);
				break;
			default:
				break;
		}
		setFilteredItems(filteredItemsValue);
	};

	const handleChangeDropdown = (filterList, value) => {
		let filterListValue = [];
		const activeFiltersValue = value;
		let filteredItemsValue = allItems;
		switch (filterList) {
			case "channelFilters":
				filterListValue = channelFilters;
				break;
			case "groupFilters":
				filterListValue = groupFilters;
				break;
			case "stateFilters":
				filterListValue = stateFilters;
				break;
			default:
				break;
		}
		const toggledFilterList = map(filterListValue, (item) => {
			if (includes(activeFiltersValue, item.id)) return { ...item, selected: true };
			return { ...item, selected: false };
		});
		switch (filterList) {
			case "channelFilters":
				filteredItemsValue = getChatsFilteredData(allItems, activeFiltersValue, activeGroupFilters, activeStateFilters, search);
				setChannelFilters(toggledFilterList);
				setActiveChannelFilters(activeFiltersValue);
				break;
			case "groupFilters":
				filteredItemsValue = getChatsFilteredData(allItems, activeChannelFilters, activeFiltersValue, activeStateFilters, search);
				setGroupFilters(toggledFilterList);
				setActiveGroupFilters(activeFiltersValue);
				break;
			case "stateFilters":
				filteredItemsValue = getChatsFilteredData(allItems, activeChannelFilters, activeGroupFilters, activeFiltersValue, search);
				setStateFilters(toggledFilterList);
				setActiveStateFilters(activeFiltersValue);
				break;
			default:
				break;
		}
		setFilteredItems(filteredItemsValue);
	};

	const handlePress = (item) => {
		if (showHistoricToday) {
			setHistoricTodayChat(item);
			dispatch(setCurrentChat(item.id, true));
			dispatch(getChatDetails(item.id, true));
			if (isMobile) {
				goChat();
			}
			return;
		}
		if (historicTodayChat) {
			setHistoricTodayChat(null);
		}
		if (!isHistoric && (item.userId === null || item.userId === 0) && permissions.allowTakeChat) {
			if (includes(["En Línea", "No Asigna Chat"], userState)) {
				dispatch(takeChat(item.id));
				if (isMobile) {
					goChat();
				} else {
					goList();
				}
			} else {
				dispatch(displayNotification({ text: `Tu estado actual "${userState}" no permite tomar conversaciones.`, isError: true }));
			}
		} else {
			dispatch(setCurrentChat(item.id, isHistoric));
			dispatch(getChatDetails(item.id, isHistoric));
			if (isMobile) {
				goChat();
			} else {
				goList();
			}
		}
	};

	const renderEmptyChat = () => {
		return <EmptyList text="No Hay Conversaciones" icon="comment alternate outline" />;
	};

	const renderChatCards = ({ key, style, index }) => {
		const isSelected = historicTodayChat ? historicTodayChat.id === filteredItems[index].id : currentChatId === filteredItems[index].id;
		return (
			<ChatCard
				key={key}
				style={style}
				isHistoric={showHistoricToday || isHistoric}
				isSelected={isSelected}
				chat={filteredItems[index]}
				dateField={dateField}
				onPress={() => {
					return handlePress(filteredItems[index]);
				}}
			/>
		);
	};

	const handleRefresh = () => {
		const filters = {
			agents,
			contact: contacts && contacts.id ? contacts.id : null,
			channels: channelfilters,
			groups: groupsfilters,
			tags: tagsSelected,
			closeState: stateValue,
			startDate,
			endDate,
			dateField,
			text: note,
			orderKey: orderBy,
			orderDir: orderDirection,
		};

		if (tabValue) {
			filters.closeTab = tabValue;
		} else {
			filters.closeTab = null;
		}

		dispatch(getChatsHistoric(filters));
	};

	const renderPrintChats = () => {
		return (
			<>
				{filteredItems.map((chat) => {
					return (
						<div className={styles.printCardContainer} key={chat.id}>
							<ChatCard
								chat={chat}
								isHistoric={showHistoricToday || isHistoric}
								isSelected={false}
								dateField={dateField}
								onPress={() => {}}
							/>
						</div>
					);
				})}
			</>
		);
	};

	const handleOpenHistoricToday = async () => {
		const today = Moment().format("YYYY-MM-DD");
		const filters = {
			agents: [],
			contact: null,
			channels: [],
			groups: [],
			tags: [],
			closeState: "",
			startDate: today,
			endDate: today,
			dateField: "closeDate",
			text: "",
			orderKey: "closeDate",
			orderDir: "desc",
			closeTab: null,
		};
		try {
			const response = await Api.post("/chats/searchHistory", filters);
			if (response.data.success) {
				const { chats: historicChats, messages: historicMessages } = response.data.data;
				const uniqueChats = uniqBy(historicChats, "id");
				const processedChats = map(uniqueChats, (chat) => {
					const chatMessages = filter(historicMessages, (msg) => {
						return msg.chatId === chat.id;
					});
					const lastMsg = !isEmpty(chatMessages)
						? reduce(
								chatMessages.filter((item) => {
									return !item.autogestion;
								}),
								(max, msg) => {
									return max?.timestamp > msg?.timestamp ? max : msg;
								}
						  )
						: null;
					let state = "Cliente";
					let stateIndex = 2;
					if (lastMsg && !lastMsg.sentByAgent) {
						state = "Atender";
						stateIndex = 1;
					}
					return { ...chat, message: lastMsg, state, stateIndex, messages: chatMessages, hasFetched: true, endFetching: true };
				});
				setHistoricTodayItems(processedChats);
				setShowHistoricToday(true);
				setFilteredItems(processedChats);
			}
		} catch (error) {
			dispatch(displayNotification({ text: "Error al cargar el histórico del día", isError: true }));
		}
	};

	const handleBackToActiveChats = () => {
		setShowHistoricToday(false);
		setHistoricTodayItems([]);
		setFilteredItems(allItems);
		setHistoricTodayChat(null);
		dispatch(setCurrentChat(0, false));
	};

	const handleHistoricTodaySuccess = () => {
		if (historicTodayChat) {
			const updatedItems = historicTodayItems.filter((item) => {
				return item.id !== historicTodayChat.id;
			});
			setHistoricTodayItems(updatedItems);
			setFilteredItems(updatedItems);
			setHistoricTodayChat(null);
		}
	};

	const handleCloseChatsFilter = () => {
		setIsFirstLoad(false);
		setOpenChatsFilter(false);
		hasOpenedFilterOnce.current = true;
	};

	const platformName = get(currentChat, "channel.label", "");

	const isShowingHistoricToday = showHistoricToday && !isHistoric;

	return (
		<div className={`${styles.container} ${isShowingHistoricToday ? styles.containerHistoric : ""}`}>
			{!isHistoric && (
				<div ref={accordionContent} className={styles.filterContainer}>
					{!showHistoricToday && (
						<div className={styles.filterBox}>
							<FilterBox
								title="Plantilla"
								count={tagCount}
								isSelected={isTagActive}
								position="left"
								onClick={() => {
									return handleBoxClick("Tag");
								}}
							/>
							<FilterBox
								title="Tra/Lib"
								withColor
								count={actionCount}
								isSelected={isActionActive}
								position="centerLeft"
								onClick={() => {
									return handleBoxClick("Action");
								}}
							/>
							<FilterBox
								title="Nuevo"
								count={newCount}
								isSelected={isNewActive}
								position="centerLeft"
								onClick={() => {
									return handleBoxClick("Nuevo");
								}}
							/>
							<FilterBox
								title="Atender"
								count={agentCount}
								isSelected={isAgentActive}
								position="center"
								onClick={() => {
									return handleBoxClick("Atender");
								}}
							/>
							<FilterBox
								title="Cliente"
								count={clientCount}
								isSelected={isClientActive}
								position="centerRight"
								onClick={() => {
									return handleBoxClick("Cliente");
								}}
							/>
							<FilterBox
								title="Todos"
								count={allCount}
								isSelected={isEmpty(activeStateFilters) || isEqualItems(activeStateFilters, staticFilters)}
								position="right"
								onClick={() => {
									return handleBoxClick("Todos");
								}}
							/>
						</div>
					)}
					<Accordion className={styles.accordionContainer}>
						<div className={styles.printButtonContainer}>
							<div className={styles.buttonsSpacer}>
								<Button
									className={styles.printButton}
									icon="print"
									onClick={() => {
										setPrintAction(true);
									}}
								/>
								<Button className={styles.printButton} icon={sortInverted ? "sort amount down" : "sort amount up"} onClick={toggleSort} />
							</div>

							<Accordion.Title className={styles.accordionTitle} active={isFiltersOpen} onClick={handleToggleFilters}>
								<div>
									Filtros Avanzados
									<Icon className={styles.accordionIcon} name="filter" />
								</div>
							</Accordion.Title>
							<div>
								{permissions?.allowTransferMultipleChats && (
									<Popup
										content="Transferir"
										position="bottom center"
										trigger={
											<Button
												className={styles.printButton}
												onClick={() => {
													setOpenChatListTableModal(true);
												}}>
												<Icon className={styles.menuIcon} name="exchange" />
											</Button>
										}
									/>
								)}
								{!showHistoricToday && (
									<Popup
										className={styles.menuPopup}
										on="click"
										position="bottom right"
										trigger={
											<Button className={styles.newChatButton}>
												<Icon name="ellipsis vertical" className={styles.newChatIcon} />
											</Button>
										}>
										<Popup.Content>
											<Menu vertical className={styles.menuVertical}>
												<Menu.Item className={styles.menuItem} onClick={handleOpenHistoricToday}>
													<Icon className={styles.menuIcon} name="history" />
													Atendidos del día
												</Menu.Item>
												<Menu.Item
													className={styles.menuItem}
													onClick={() => {
														setOpenChatPlantillaModal(true);
													}}>
													<Icon className={styles.menuIcon} name="plus" />
													Chat
												</Menu.Item>
											</Menu>
										</Popup.Content>
									</Popup>
								)}
								{showHistoricToday && (
									<Button
										className={styles.newChatButton}
										onClick={() => {
											return setOpenChatPlantillaModal(true);
										}}>
										<Icon name="plus" className={styles.newChatIcon} />
									</Button>
								)}
							</div>
						</div>
						{isFiltersOpen && (
							<Accordion.Content active={isFiltersOpen}>
								<SectionFilter
									title="Canales"
									isChecked={isChannelCheckedAll}
									options={getDropdownOptions(channelFilters)}
									value={activeChannelFilters}
									onChangeCheck={() => {
										return handleCheckFilter("channelFilters");
									}}
									onChangeDropdown={(e, { value }) => {
										return handleChangeDropdown("channelFilters", value);
									}}
								/>
								<SectionFilter
									title="Grupos"
									isChecked={isGroupCheckedAll}
									options={getDropdownOptions(groupFilters)}
									value={activeGroupFilters}
									onChangeCheck={() => {
										return handleCheckFilter("groupFilters");
									}}
									onChangeDropdown={(e, { value }) => {
										return handleChangeDropdown("groupFilters", value);
									}}
								/>
								<SectionFilter
									title="Estados"
									isChecked={isStateCheckedAll}
									options={getDropdownOptions(stateFilters)}
									value={activeStateFilters}
									onChangeCheck={() => {
										return handleCheckFilter("stateFilters");
									}}
									onChangeDropdown={(e, { value }) => {
										return handleChangeDropdown("stateFilters", value);
									}}
								/>
							</Accordion.Content>
						)}
					</Accordion>
				</div>
			)}
			{isHistoric && (
				<div className={styles.contentFirstH}>
					<Button
						primary
						className={styles.filterButton}
						icon="pause"
						onClick={() => {
							return setOpenChatsFilter(true);
						}}>
						Filtro - ({filteredItems.length})
						<Icon className={styles.filterIcon} name="filter" />
					</Button>
					<Button className={styles.buttonRefreshH} onClick={handleRefresh}>
						<Icon name="refresh" />
					</Button>
					<Button
						className={styles.printButtonH}
						onClick={() => {
							setPrintAction(true);
						}}>
						<Icon name="print" />
					</Button>
					{!showHistoricToday && (
						<Popup
							className={styles.menuPopup}
							on="click"
							position="bottom right"
							trigger={
								<Button className={styles.newChatButtonH}>
									<Icon name="ellipsis vertical" className={styles.newChatIcon} />
								</Button>
							}>
							<Popup.Content>
								<Menu vertical className={styles.menuVertical}>
									<Menu.Item className={styles.menuItem} onClick={handleOpenHistoricToday}>
										<Icon className={styles.menuIcon} name="history" />
										Atendidos del día
									</Menu.Item>
									<Menu.Item
										className={styles.menuItem}
										onClick={() => {
											setOpenChatPlantillaModal(true);
										}}>
										<Icon className={styles.menuIcon} name="plus" />
										Crear chat
									</Menu.Item>
								</Menu>
							</Popup.Content>
						</Popup>
					)}
					{showHistoricToday && (
						<Button
							className={styles.newChatButtonH}
							onClick={() => {
								return setOpenChatPlantillaModal(true);
							}}>
							<Icon name="plus" className={styles.newChatIcon} />
						</Button>
					)}
				</div>
			)}
			{showHistoricToday && (
				<div className={styles.historicTodayContainer}>
					<Form.Checkbox
						toggle
						label="Ver todos"
						checked={showAllChats}
						className={styles.smallToggle}
						onChange={(e, { checked }) => {
							return setShowAllChats(checked);
						}}
					/>
					<div className={styles.historicTodayHeaderRight}>
						<span className={styles.historicTodayTitle}>Atendidos del día ({filteredItems.length})</span>
						<Button className={styles.backButton} onClick={handleBackToActiveChats}>
							<Icon name="arrow left" className={styles.volver} />
							Volver
						</Button>
					</div>
				</div>
			)}
			<div className={styles.searchContainer}>
				<Input rounded icon="search" placeholder="Buscar Conversación o Contacto" value={search} onChange={handleSearch} />
			</div>
			<div className={styles.chatCardsContainer} ref={printRef}>
				<AutoSizer>
					{({ height, width }) => {
						return (
							<List
								data={filteredItems}
								height={height}
								width={width}
								overscanRowCount={3}
								rowCount={filteredItems.length}
								rowHeight={105}
								rowRenderer={renderChatCards}
								noRowsRenderer={renderEmptyChat}
							/>
						);
					}}
				</AutoSizer>
				{printAction && (
					<div className={styles.printGridRe} ref={printRef}>
						{renderPrintChats()}
					</div>
				)}
			</div>

			<PrintRender
				hiddenButon
				printAction={printAction}
				printActionRetry={() => {
					setPrintAction(false);
				}}
				printHeader={{
					company: companyName,
					user: userName,
					title: `Lista de Chats - (${filteredItems.length})`,
				}}
				platform={platformName}
				renderData={printRef}
				hideClientInfo
				forceLeftUserInfos
			/>

			{openChatPlantillaModal && (
				<ChatPlantillaModal
					search={cmdSearch}
					currentChat={isHistoric || showHistoricToday ? currentChat : null}
					onClose={() => {
						setOpenChatPlantillaModal(false);
					}}
					onSuccess={showHistoricToday ? handleHistoricTodaySuccess : undefined}
					channel={isHistoric || showHistoricToday ? currentChat?.channel : null}
					contact={isHistoric || showHistoricToday ? currentChat?.contact : null}
					isReadOnly={(isHistoric || showHistoricToday) && !!currentChat}
					isHistoric={isHistoric || showHistoricToday}
					modalCrud="Add"
				/>
			)}

			{openChatListTableModal && (
				<ChatListTableModal
					open={openChatListTableModal}
					onClose={() => {
						setOpenChatListTableModal(false);
					}}
					chats={filteredItems}
					isHistoric={isHistoric}
				/>
			)}

			{openChatsFilter && (
				<ChatsFilter
					dispatch={dispatch}
					channels={channels}
					groups={groups}
					onClose={() => {
						return setOpenChatsFilter(false);
					}}
					onRefresh={handleRefresh}
					onApplyFilters={handleCloseChatsFilter}
					channelfilters={channelfilters}
					setChannelfilters={setChannelfilters}
					groupsfilters={groupsfilters}
					setGroupsfilters={setGroupsfilters}
					tagsSelected={tagsSelected}
					setTagsSelected={setTagsSelected}
					tabValue={tabValue}
					setTabValue={setTabValue}
					stateValue={stateValue}
					setStateValue={setStateValue}
					orderBy={orderBy}
					setOrderBy={setOrderBy}
					orderDirection={orderDirection}
					setOrderDirection={setOrderDirection}
					startDate={startDate}
					setStartDate={setStartDate}
					endDate={endDate}
					setEndDate={setEndDate}
					contacts={contacts}
					setContacts={setContacts}
					agents={agents}
					setAgents={setAgents}
					note={note}
					setNote={setNote}
					dateField={dateField}
					setDateField={setDateField}
				/>
			)}
		</div>
	);
};

ChatList.propTypes = {
	dispatch: PropTypes.func,
	isHistoric: PropTypes.bool,
	channels: PropTypes.array,
	groups: PropTypes.array,
	chats: PropTypes.array,
	currentChatId: PropTypes.number,
	currentChat: PropTypes.object,
	permissions: PropTypes.object,
	companyName: PropTypes.string,
	userName: PropTypes.string,
	userId: PropTypes.number,
	userState: PropTypes.string,
	cmdSearch: PropTypes.string,
	sortInverted: PropTypes.bool,
	toggleSort: PropTypes.func,
	setHistoricTodayChat: PropTypes.func,
	historicTodayChat: PropTypes.object,
};

function mapStateToProps({ user, company }) {
	return {
		groups: user.groups,
		permissions: user.permissions,
		userName: user.name,
		userId: user.userId,
		userState: user.state,
		companyName: company.name,
		channels: company.channels,
	};
}

export default connect(mapStateToProps)(ChatList);
